import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ResponsiveTable, ResponsiveTableColumn } from '@/components/common/ResponsiveTable';
import { Alert } from '@/components/common/Alert';
import { productService } from '@/services/product.service';
import { categoryService } from '@/services/category.service';
import { Product } from '@/types/product.types';
import { CategoryWithSubCategories } from '@/types/category.types';
import '@/styles/dashboard.css';

const ITEMS_PER_PAGE = 10;

export const ListeProduits = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<CategoryWithSubCategories[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filtres et recherche
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedStockStatus, setSelectedStockStatus] = useState<string>('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await productService.getAll();
      setProducts(data);
      setError(null);
    } catch (err) {
      console.error('Erreur lors du chargement des produits:', err);
      setError(t('adminProductsList.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await categoryService.getAll();
      setCategories(data);
    } catch (err) {
      console.error('Erreur lors du chargement des catégories:', err);
    }
  };

  // Filtrer et paginer les produits
  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    // Recherche par nom
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(query) ||
        product.sku?.toLowerCase().includes(query) ||
        product.description?.toLowerCase().includes(query)
      );
    }

    // Filtre par catégorie
    if (selectedCategory) {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }

    // Filtre par statut
    if (selectedStatus) {
      filtered = filtered.filter(product => product.status === selectedStatus);
    }

    // Filtre par statut de stock
    if (selectedStockStatus) {
      filtered = filtered.filter(product => product.stock_status === selectedStockStatus);
    }

    return filtered;
  }, [products, searchQuery, selectedCategory, selectedStatus, selectedStockStatus]);

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

  // Réinitialiser la page quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, selectedStatus, selectedStockStatus]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const getStatusClass = (status: string) => {
    const statusMap: Record<string, string> = {
      'published': 'stock',
      'draft': 'pending',
      'archived': 'stock-out'
    };
    return statusMap[status] || 'pending';
  };

  const getStatusText = (status: string) => {
    const statusMap: Record<string, string> = {
      'published': t('adminProductsList.published'),
      'draft': t('adminProductsList.draft'),
      'archived': t('adminProductsList.archived')
    };
    return statusMap[status] || status;
  };

  const getStockClass = (stockStatus: string) => {
    const stockMap: Record<string, string> = {
      'in-stock': 'stock',
      'out-of-stock': 'stock-out',
      'pre-order': 'pending'
    };
    return stockMap[stockStatus] || 'stock';
  };

  const handleDelete = async (id: number, name: string) => {
    if (window.confirm(t('adminProductsList.deleteConfirm', { name }))) {
      try {
        setLoading(true);
        await productService.delete(id);
        await loadProducts();
        setError(null);
      } catch (err: any) {
        console.error('Erreur lors de la suppression:', err);
        const errorMessage = err?.response?.data?.error || err?.message || 'Erreur lors de la suppression du produit';
        
        if (err?.response?.status === 401) {
          alert('Votre session a expiré. Veuillez vous reconnecter.');
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
        } else if (err?.response?.status === 403) {
          alert('Vous n\'avez pas les permissions nécessaires pour supprimer un produit.');
        } else {
          alert(`Erreur lors de la suppression: ${errorMessage}`);
        }
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = async (id: number) => {
    try {
      // Vérifier que le produit existe avant de naviguer
      await productService.getById(id);
      navigate(`/admin/ajouter-produit?id=${id}`);
    } catch (err: any) {
      console.error('Erreur lors de la récupération du produit:', err);
      const errorMessage = err?.response?.data?.error || err?.message || 'Erreur lors de la récupération du produit';
      
      if (err?.response?.status === 401) {
        alert('Votre session a expiré. Veuillez vous reconnecter.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else if (err?.response?.status === 403) {
        alert('Vous n\'avez pas les permissions nécessaires pour modifier un produit.');
      } else if (err?.response?.status === 404) {
        alert('Produit non trouvé. Il a peut-être été supprimé.');
        await loadProducts(); // Recharger la liste
      } else {
        alert(`Erreur: ${errorMessage}`);
      }
    }
  };

  const handleView = (id: number) => {
    navigate(`/produit-detail?id=${id}`);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('');
    setSelectedStatus('');
    setSelectedStockStatus('');
    setCurrentPage(1);
  };

  const hasActiveFilters = searchQuery || selectedCategory || selectedStatus || selectedStockStatus;

  // Récupérer toutes les catégories uniques depuis les produits
  const allCategories = useMemo(() => {
    const categorySet = new Set<string>();
    products.forEach(product => {
      if (product.category) {
        categorySet.add(product.category);
      }
    });
    return Array.from(categorySet).sort();
  }, [products]);

  return (
    <div className="table-card">
              <div className="table-header">
                <h3 className="table-title">{t('adminProductsList.title', { count: filteredProducts.length })}</h3>
                <div className="flex gap-2 items-center">
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="btn btn-secondary btn-sm"
                    >
                      {t('adminProductsList.resetFilters')}
                    </button>
                  )}
                  <button
                    onClick={() => navigate('/admin/ajouter-produit')}
                    className="btn btn-primary btn-sm"
                  >
                    + {t('adminProductsList.addProduct')}
                  </button>
                </div>
              </div>

              {/* Barre de recherche et filtres */}
              <div className="filters-bar">
                <div className="form-group-flex">
                  <label className="form-label">{t('adminProductsList.search')}</label>
                  <input
                    type="text"
                    placeholder={t('adminProductsList.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div style={{ minWidth: '180px' }}>
                  <label className="form-label">{t('adminProductsList.category')}</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="form-select"
                  >
                    <option value="">{t('adminProductsList.allCategories')}</option>
                    {allCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div style={{ minWidth: '150px' }}>
                  <label className="form-label">{t('adminProductsList.status')}</label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="form-select"
                  >
                    <option value="">{t('adminProductsList.allStatuses')}</option>
                    <option value="published">{t('adminProductsList.published')}</option>
                    <option value="draft">{t('adminProductsList.draft')}</option>
                    <option value="archived">{t('adminProductsList.archived')}</option>
                  </select>
                </div>
                <div style={{ minWidth: '150px' }}>
                  <label className="form-label">{t('adminProductsList.stock')}</label>
                  <select
                    value={selectedStockStatus}
                    onChange={(e) => setSelectedStockStatus(e.target.value)}
                    className="form-select"
                  >
                    <option value="">{t('adminProductsList.allStock')}</option>
                    <option value="in-stock">{t('adminProductsList.inStock')}</option>
                    <option value="out-of-stock">{t('adminProductsList.outOfStock')}</option>
                    <option value="pre-order">{t('adminProductsList.preOrder')}</option>
                  </select>
                </div>
              </div>

              {error ? (
                <Alert type="error" message={error} />
              ) : (
                <ResponsiveTable
                columns={[
                  {
                    key: 'image',
                    label: t('adminProductsList.image'),
                    mobileHidden: true,
                    render: (product) => (
                      <div className="product-image-cell">
                        <img
                          src={product.image_url || '/images/bottle.png'}
                          alt={product.name}
                          className="product-table-image"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/images/bottle.png';
                          }}
                        />
                      </div>
                    ),
                  },
                  {
                    key: 'name',
                    label: t('adminProductsList.productName'),
                    render: (product) => (
                      <div className="product-name-cell">
                        <strong>{product.name}</strong>
                        {product.category && (
                          <div className="product-category">{product.category}</div>
                        )}
                      </div>
                    ),
                  },
                  {
                    key: 'sku',
                    label: 'SKU',
                    mobileLabel: 'Référence',
                    render: (product) => product.sku || 'N/A',
                  },
                  {
                    key: 'price',
                    label: t('adminProductsList.price'),
                    render: (product) => (
                      <div className="product-price-cell">
                        {product.reduced_price && (
                          <span className="price-reduced">{formatPrice(product.reduced_price)} €</span>
                        )}
                        <span className={product.reduced_price ? 'price-original' : 'price-current'}>
                          {formatPrice(product.price)} €
                        </span>
                      </div>
                    ),
                  },
                  {
                    key: 'stock',
                    label: t('adminProductsList.stock'),
                    render: (product) => (
                      <span className={`stock-badge ${getStockClass(product.stock_status)}`}>
                        {product.stock_unlimited ? t('adminProductsList.unlimited') : (product.stock_quantity || 0)}
                      </span>
                    ),
                  },
                  {
                    key: 'status',
                    label: t('adminProductsList.statusCol'),
                    render: (product) => (
                      <span className={`status-badge ${getStatusClass(product.status)}`}>
                        {getStatusText(product.status)}
                      </span>
                    ),
                  },
                  {
                    key: 'created_at',
                    label: t('adminProductsList.createdAt'),
                    mobileLabel: 'Créé le',
                    mobileHidden: true,
                    render: (product) => formatDate(product.created_at),
                  },
                  {
                    key: 'actions',
                    label: t('adminProductsList.actions'),
                    mobileHidden: true,
                    render: (product) => (
                      <div className="product-actions" style={{ position: 'relative', zIndex: 10 }}>
                        <button
                          className="action-btn action-edit"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleEdit(product.id);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          title={t('adminProductsList.edit')}
                          type="button"
                          style={{ position: 'relative', zIndex: 11, pointerEvents: 'auto' }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                          </svg>
                        </button>
                        <button
                          className="action-btn action-delete"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDelete(product.id, product.name);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          title={t('adminProductsList.delete')}
                          type="button"
                          style={{ position: 'relative', zIndex: 11, pointerEvents: 'auto' }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                          </svg>
                        </button>
                        <button
                          className="action-btn action-view"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleView(product.id);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          title={t('adminReviews.view')}
                          type="button"
                          style={{ position: 'relative', zIndex: 11, pointerEvents: 'auto' }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                          </svg>
                        </button>
                      </div>
                    ),
                  },
                ]}
                data={paginatedProducts}
                loading={loading}
                loadingMessage={t('adminProductsList.loading')}
                emptyMessage={
                  hasActiveFilters
                    ? t('adminProductsList.noFilters')
                    : t('adminProductsList.empty')
                }
                mobileCardTitle={(product) => (
                  <div>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{product.name}</div>
                    {product.category && (
                      <div style={{ fontSize: '12px', color: '#666' }}>{product.category}</div>
                    )}
                  </div>
                )}
                mobileCardActions={(product) => (
                  <div className="product-actions" style={{ display: 'flex', gap: '8px', position: 'relative', zIndex: 10 }}>
                    <button
                      className="action-btn action-edit"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleEdit(product.id);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                      title={t('adminProductsList.edit')}
                      type="button"
                      style={{ position: 'relative', zIndex: 11, pointerEvents: 'auto' }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                      </svg>
                    </button>
                    <button
                      className="action-btn action-view"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleView(product.id);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                      title={t('adminProductsList.view')}
                      type="button"
                      style={{ position: 'relative', zIndex: 11, pointerEvents: 'auto' }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                      </svg>
                    </button>
                    <button
                      className="action-btn action-delete"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDelete(product.id, product.name);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                      title={t('adminProductsList.delete')}
                      type="button"
                      style={{ position: 'relative', zIndex: 11, pointerEvents: 'auto' }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                      </svg>
                    </button>
                  </div>
                )}
                tableClassName="products-table"
              />
              )}

              {/* Pagination */}
              {!error && totalPages > 1 && (
                <div className="pagination-container" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '24px',
                  padding: '16px',
                  borderTop: '1px solid #e0e0e0'
                }}>
                  <div className="pagination-info" style={{ fontSize: '14px', color: '#666' }}>
                    {t('adminProductsList.displaying', { start: startIndex + 1, end: Math.min(endIndex, filteredProducts.length), total: filteredProducts.length })}
                  </div>
                  <div className="pagination-controls" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      {t('adminProductsList.previous')}
                    </button>
                    <div className="pagination-pages" style={{ display: 'flex', gap: '4px' }}>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
    <>

                            <button
                              key={page}
                              className={`btn ${currentPage === page ? 'btn-primary' : 'btn-secondary'} btn-sm`}
                              onClick={() => setCurrentPage(page)}
                              style={{ minWidth: '36px' }}
                            >
                              {page}
                            </button>
                          
    </>
  );
                        } else if (page === currentPage - 2 || page === currentPage + 2) {
                          return <span key={page} style={{ padding: '0 4px' }}>...</span>;
                        }
                        return null;
                      })}
                    </div>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      {t('adminProductsList.next')}
                    </button>
                  </div>
                </div>
              )}
            </div>
  );
};
